import type { AvailabilityGroupClientDto, OfferAdminDto } from 'service-api/src/types/api'

type TAvailability = NonNullable<AvailabilityGroupClientDto['availabilities']>[number]

// Callers check the location's waitlist settings (subscriber group and service
// type booking conf) before this; it decides only which availabilities qualify.
export const waitlistEligible = (availability?: TAvailability[]) => {
  if (!availability) return false
  const firstAvailability = availability?.[0]
  if (!firstAvailability) return false
  // A Hatch rule that stated an action decides: only a Hatch `waitlist` qualifies.
  if (hasHatchBlockFlavor(firstAvailability)) return isHatchWaitlist(firstAvailability)
  const { unavailableType, unavailableReason } = firstAvailability
  if (unavailableType === 'INVENTORY') return true
  // MBV restriction is allowed to waitlist
  if (unavailableType === 'RESTRICTION' && unavailableReason?.type === 'PRICE') return true
  // Blackout restriction is allowed to waitlist
  if (unavailableType === 'RESTRICTION' && unavailableReason?.type === 'OCCUPANCY') return true
  return false
}

/**
 * Hatch-only fields on the unavailable reason. They are not declared on the
 * generated DTO, so this encapsulates the cast.
 */
const hatchReason = (availability?: Partial<TAvailability> | null) =>
  availability?.unavailableReason as { source?: string; blockFlavor?: string } | null | undefined

/**
 * True when a Hatch rule explicitly stated an action (`blockFlavor`: hide,
 * block or waitlist) for this availability. Only then does Hatch decide; a
 * Hatch-sourced reason without a `blockFlavor` falls back to the goose-api rules.
 */
export const hasHatchBlockFlavor = (availability?: Partial<TAvailability> | null): boolean => {
  const reason = hatchReason(availability)
  return reason?.source === 'hatch' && !!reason?.blockFlavor
}

/** True when a Hatch rule put this availability on the waitlist (`blockFlavor: 'waitlist'`). */
export const isHatchWaitlist = (availability?: Partial<TAvailability> | null): boolean => {
  const reason = hatchReason(availability)
  return reason?.source === 'hatch' && reason?.blockFlavor === 'waitlist'
}

export const usesRooms = (offer: OfferAdminDto) => {
  return (
    offer.locationServiceType?.serviceType?.name === 'boarding' ||
    offer.locationServiceType?.serviceType?.name === 'daycare'
  )
}
